/**
 * Parse date string safely without timezone conversion issues
 * @param {string|Date} dateValue - Date string (YYYY-MM-DD, ISO format, or Date object)
 * @returns {Date} - Date object set to local midnight
 */
export const parseDateLocal = (dateValue) => {
  if (dateValue instanceof Date) return dateValue;
  if (!dateValue) return new Date();

  const dateStr = String(dateValue);

  // Pattern 1: Date-only string (YYYY-MM-DD)
  const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const dateOnlyMatch = dateStr.match(dateOnlyPattern);

  if (dateOnlyMatch) {
    // Parse as local date without timezone conversion
    const year = parseInt(dateOnlyMatch[1], 10);
    const month = parseInt(dateOnlyMatch[2], 10) - 1; // months are 0-indexed
    const day = parseInt(dateOnlyMatch[3], 10);
    return new Date(year, month, day);
  }

  // Pattern 2: ISO format with time (YYYY-MM-DDTHH:MM:SS or YYYY-MM-DDTHH:MM:SS.sssZ)
  // Extract just the date part and ignore timezone to prevent day shift
  const isoPattern = /^(\d{4})-(\d{2})-(\d{2})T/;
  const isoMatch = dateStr.match(isoPattern);

  if (isoMatch) {
    // Extract date components and create local date (ignore time/timezone)
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10) - 1; // months are 0-indexed
    const day = parseInt(isoMatch[3], 10);
    return new Date(year, month, day);
  }

  // Pattern 3: Handle any other format - extract date part safely
  // This handles formats like "2027-01-01 00:00:00" etc.
  const generalPattern = /^(\d{4})-(\d{2})-(\d{2})/;
  const generalMatch = dateStr.match(generalPattern);

  if (generalMatch) {
    const year = parseInt(generalMatch[1], 10);
    const month = parseInt(generalMatch[2], 10) - 1;
    const day = parseInt(generalMatch[3], 10);
    return new Date(year, month, day);
  }

  // Fallback: parse and extract local date components to avoid timezone issues
  const parsed = new Date(dateValue);
  if (!isNaN(parsed.getTime())) {
    // If the string contains 'Z' or timezone offset, it was parsed as UTC
    // We need to extract the UTC date components instead
    if (dateStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
      return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
    }
  }

  return parsed;
};

/**
 * Transform frontend Gantt tasks to backend Phase and EemProjectMeasure format
 * @param {Array} phases - Array of phase tasks (parent === 0)
 * @param {Array} measures - Array of measure tasks (parent !== 0)
 * @param {number} projectId - Project ID
 * @returns {Object} - Transformed data ready for backend
 */
export const transformToBackendFormat = (phases, measures, projectId) => {
  // Transform phases to match backend Phase entity
  const transformedPhases = phases.map((phase, index) => {
    // Collect measure IDs that belong to this phase
    const measureIds = measures
      .filter(measure => measure.parent === phase.id)
      .map(measure => measure.id)
      .filter(id => id && typeof id === 'number'); // Only include valid numeric IDs

    // Helper to format date as date-only (no timezone issues)
    const formatDateLocal = (date) => {
      if (!(date instanceof Date)) return date;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    };

    // Extract ID if it exists (for updates), otherwise backend will create new
    const phaseData = {
      phaseName: phase.text,
      startDate: formatDateLocal(phase.start),
      endDate: formatDateLocal(phase.end),
      parentProject: projectId,
      phaseOrder: index + 1,
      measureIds: measureIds,
      active:true
    };

    // Include ID only if it exists and is a valid number (not a temp ID like "temp://...")
    if (phase.id && typeof phase.id === 'number') {
      phaseData.id = phase.id;
    }

    return phaseData;
  });

  // Create a map to track order counters for each phase
  const phaseOrderCounters = {};

  // Transform measures to match backend EemProjectMeasure entity
  const transformedMeasures = measures.map(measure => {
    // Start with original measure data if available
    const original = measure.originalMeasure || {};

    // Calculate eemMeasureOrder: increment counter for this phase
    const phaseId = measure.parent;
    if (!phaseOrderCounters[phaseId]) {
      phaseOrderCounters[phaseId] = 0;
    }
    phaseOrderCounters[phaseId]++;
    const measureOrder = phaseOrderCounters[phaseId];

    const measureData = {
      // Copy all other fields from original measure first
      ...original,

      // Override with required fields (these should not be overwritten)
      // A phase that has not been saved yet has a temp string id ("temp-phase-..."). The backend
      // field is a BigInteger, so sending one is a 500, and it is not needed anyway: measure to
      // phase linking is driven by phase.measureIds, not by this field.
      phase: typeof measure.parent === "number" ? measure.parent : null,
      eemMeasureOrder: measureOrder, // Order within the phase
      ecmCategoryId: original.ecmCategoryId,
      ecmDescription: original.ecmDescription || measure.text || measure.details,
      org: original.org,
      project: original.project || projectId,
      property: original.property,
    };

    // Include ID only if it exists and is a valid number
    if (measure.id && typeof measure.id === 'number') {
      measureData.id = measure.id;
    }

    return measureData;
  });

  return {
    phases: transformedPhases,
    measures: transformedMeasures
  };
};

/**
 * Transform backend Phase and EemProjectMeasure response to frontend Gantt format
 * @param {Object} backendData - Response from backend with phases and measures
 * @returns {Array} - Array of tasks in Gantt format
 */
export const transformFromBackendFormat = (backendData) => {
  const { phases, measures } = backendData;

  const tasks = [];

  // Transform phases
  if (phases && Array.isArray(phases)) {
    phases.forEach(phase => {
      tasks.push({
        id: phase.id,
        text: phase.phaseName,
        start: parseDateLocal(phase.startDate),
        end: parseDateLocal(phase.endDate),
        progress: 0,
        parent: 0,
        type: "task",
        open: true,
        isPhase: true
      });
    });
  }

  // Transform measures
  if (measures && Array.isArray(measures)) {
    measures.forEach(measure => {
      tasks.push({
        id: measure.id,
        text: measure.ecmDescription || measure.description || "Measure",
        start: parseDateLocal(measure.startDate || measure.start),
        end: parseDateLocal(measure.endDate || measure.end),
        progress: measure.progress || 0,
        parent: measure.phase || 0,
        type: "task"
      });
    });
  }

  return tasks;
};